import { msg } from '@lingui/macro'

import { TFCartBaseContent } from '~graphql/generated/clientGraphql'
import { isDefined } from '~logic/helpers/isDefined'
import { useTrans } from '~logic/i18n/useTrans'
import { CartItem } from '~ui/components/cart/CartItem'
import { CartItemSpecial } from '~ui/components/cart/CartItemSpecial'

// used to prepare data for CartItemSpecial render
type TBaseCartItem = {
  key: string
  label: PropsOf<typeof CartItemSpecial>['wareItem']['nameLabel']
  formattedValue: PropsOf<typeof CartItemSpecial>['wareItem']['price']
  onClick?: PropsOf<typeof CartItemSpecial>['wareItem']['onClick']
}

// used to prepare data for CartItem render
type TCartItemWithAmount = TBaseCartItem & {
  amount: PropsOf<typeof CartItem>['amount']
}

/**
 * Build the special items (fees, tip, ...) that should be rendered in the cart.
 */
export const getSpecialItems =
  (goods: TFCartBaseContent) =>
  (onTransferFeeClick: TBaseCartItem['onClick']) =>
  (trans: TTrans) => {
    const isPickup = goods.handoverType?.enum === `PICKUP`
    const hasTransportFee = isDefined(goods.transportFee)
    const hasHandoverType = isDefined(goods.handoverType)

    let specialItems: TBaseCartItem[] = []

    if (goods.coverPrice.value !== 0) {
      specialItems.push({
        key: `covers`,
        label: trans(msg`Covers`),
        formattedValue: goods.coverPrice.formattedValue,
      })
    }

    if (hasTransportFee && hasHandoverType && !isPickup) {
      const isFree = goods.transportFee.value === 0

      specialItems.push({
        key: `transportFee`,
        label: trans(msg`Transport fee`),
        formattedValue: isFree ? trans(msg`Free of charge`) : goods.transportFee.formattedValue,
        onClick: onTransferFeeClick,
      })
    }

    if (goods.tip.value !== 0) {
      specialItems.push({
        key: `tip`,
        label: trans(msg`Tip`),
        formattedValue: goods.tip.formattedValue,
      })
    }

    return specialItems
  }

type TTrans = ReturnType<typeof useTrans>['trans']

/**
 * Build the discount items (discounts & vouchers) that should be rendered in the cart.
 */
export const getDiscountItems = (goods: TFCartBaseContent) => (trans: TTrans) => {
  const discounts: RoA<TCartItemWithAmount> = goods.discounts.map((discount, index) => ({
    amount: 1,
    key: `${discount.code}_${index}`, // I think we can theoretically allow the user to use one promo code multiple times (the "code" doesn’t have to be unique)
    label: discount.description,
    formattedValue: discount.price.formattedValue,
  }))

  const vouchers: RoA<TCartItemWithAmount> = goods.vouchers.map((voucher, index) => ({
    amount: 1,
    key: `${voucher.code}_${index}`, // same reason as above
    label: trans(msg`Voucher ${voucher.description}`),
    formattedValue: voucher.price.formattedValue,
  }))

  return [...discounts, ...vouchers]
}
